import { Reposition } from '@/helpers/storeHelper'
import addPanel from '@/store/landing/actions/insert/panel.js'
import addSection from '@/store/landing/actions/insert/section.js'
import addWidget from '@/store/landing/actions/insert/widget.js'
import addTabs from '@/store/landing/actions/insert/tabs.js'
import addHeroSparkBanner from '@/store/landing/actions/insert/herospark-banner.js'
import { uuid } from 'vue-uuid'
import Vue from 'vue'

export default {
  addSection(
    { state, commit, dispatch },
    { id = null, parentId = null, options = {}, stack = 'undo', stackToClear = 'redo', panelCount = 1, title } = {}
  ) {
    const siblings = state.landing.filter(l => !l.path && ['Section', 'Tabs'].includes(l.type)),
      section = addSection(id || `S-${uuid.v1()}`, options, siblings, title),
      reposition = Reposition(siblings, section.options, (crr, ref) => crr >= ref, 1)

    parentId
      ? commit('insert', { item: { ...section, path: [parentId] }, parentId })
      : commit('insert', { item: section })

    Vue.nextTick(() => setTimeout(() => dispatch('ui/scrollIntoView', section.id, { root: true })))
    commit('reposition', reposition)

    let sizeLocal = 12 / panelCount

    for (let count = 0; count < panelCount; count++) {
      dispatch('addPanel', { parentId: section.id, panelSize: sizeLocal })
    }

    if (!parentId)
      dispatch(
        'history/pushToStack',
        {
          stack,
          action: 'landing/removeSection',
          params: { id: section.id },
          stackToClear,
        },
        { root: true }
      )
  },

  addSectionWithWidgets(
    { state, commit, dispatch },
    { id = null, parentId = null, options = {}, stack = 'undo', stackToClear = 'redo', widgetList = [] } = {}
  ) {
    const siblings = state.landing.filter(l => !l.path && ['Section', 'Tabs'].includes(l.type)),
      section = addSection(id || `S-${uuid.v1()}`, options, siblings),
      reposition = Reposition(siblings, section.options, (crr, ref) => crr >= ref, 1)

    parentId
      ? commit('insert', { item: { ...section, path: [parentId] }, parentId })
      : commit('insert', { item: section })

    Vue.nextTick(() => setTimeout(() => dispatch('ui/scrollIntoView', section.id, { root: true })))
    commit('reposition', reposition)

    widgetList.forEach(widget => {
      dispatch('addPanel', {
        parentId: section.id,
        panelSize: 12,
        widget,
      })
    })

    if (!parentId)
      dispatch(
        'history/pushToStack',
        {
          stack,
          action: 'landing/removeSection',
          params: { id: section.id },
          stackToClear,
        },
        { root: true }
      )
  },

  addHeroSparkBanner(
    { state, commit, dispatch },
    { id = null, options = {}, stack = 'undo', stackToClear = 'redo' } = {}
  ) {
    const siblings = state.landing.filter(l => ['Section', 'Tabs'].includes(l.type)),
      hsBanner = addHeroSparkBanner(id || `HS-${uuid.v1()}`, options, siblings),
      reposition = Reposition(siblings, hsBanner.options, (crr, ref) => crr >= ref, 1)

    commit('insert', { item: hsBanner })

    Vue.nextTick(() => setTimeout(() => dispatch('ui/scrollIntoView', hsBanner.id, { root: true })))
    commit('reposition', reposition)

    dispatch(
      'history/pushToStack',
      {
        stack,
        action: 'landing/removeHeroSparkBanner',
        params: { id: hsBanner.id },
        stackToClear,
      },
      { root: true }
    )
  },

  addPanel(
    { commit, getters, dispatch },
    {
      id = null,
      parentId = null,
      options = {},
      stack = 'undo',
      stackToClear = 'redo',
      panelSize = 12,
      widget = null,
      scrollIntoView = true,
    } = {}
  ) {
    if (!parentId) {
      return
    }

    const parent = getters.objectById(parentId),
      canAddPanel = !parent.children.some(c => c.startsWith('W-'))

    if (canAddPanel) {
      const panelId = id || `P-${uuid.v1()}`

      const siblings = parent.children.map(c => getters.objectById(c)),
        path = [...(parent.path || []), parentId],
        position = siblings.length,
        panel = addPanel(panelId, options, path, position),
        reposition = Reposition(siblings, panel.options, (crr, ref) => crr >= ref, 1)

      commit('insert', { item: panel, parentId })

      if (scrollIntoView) Vue.nextTick(() => setTimeout(() => dispatch('ui/scrollIntoView', panel.id, { root: true })))

      dispatch('updateOptions', {
        id: panel.id,
        parentId,
        path: 'lg.size',
        value: panelSize,
        pushUpdateToStack: false,
      })

      commit('reposition', reposition)
      dispatch(
        'history/pushToStack',
        {
          stack,
          action: 'landing/removePanel',
          params: { id: panel.id },
          stackToClear,
        },
        { root: true }
      )

      if (widget) {
        dispatch('addWidget', {
          parentId: panelId,
          scrollIntoView: scrollIntoView,
          type: widget.type,
          options: widget.options,
        })
      }

      return panelId
    }
  },

  addWidget(
    { commit, getters, dispatch },
    {
      id = null,
      parentId = null,
      type = '',
      options = {},
      stack = 'undo',
      stackToClear = 'redo',
      scrollIntoView = true,
    } = {}
  ) {
    if (!parentId) {
      return
    }

    const parent = getters.objectById(parentId),
      canAddWidget = parent.children.length === 0

    if (canAddWidget) {
      const widget = addWidget(id || `W-${uuid.v1()}`, type, [...(parent.path || []), parentId], options)
      commit('insert', { item: widget, parentId })
      commit('updateTitle', { id: parentId, value: widget.title })

      if (scrollIntoView) Vue.nextTick(() => setTimeout(() => dispatch('ui/scrollIntoView', widget.id, { root: true })))

      dispatch(
        'history/pushToStack',
        {
          stack,
          action: 'landing/removeWidget',
          params: { id: widget.id },
          stackToClear,
        },
        { root: true }
      )
    }
  },

  addTabs({ state, commit, dispatch }, { id = null, options = {}, stack = 'undo', stackToClear = 'redo' } = {}) {
    const siblings = state.landing.filter(l => !l.path && ['Section', 'Tabs'].includes(l.type)),
      tabs = addTabs(id || `T-${uuid.v1()}`, options, siblings),
      reposition = Reposition(siblings, tabs.options, (crr, ref) => crr >= ref, 1)

    commit('insert', { item: tabs })

    dispatch('addSectionWithWidgets', {
      parentId: tabs.id,
      widgetList: [
        { type: 'Video' },
        {
          type: 'Text',
          options: {
            content:
              '<strong>Webinar 1</strong><br>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Etiam facilisis tempus erat, vitae lobortis orci elementum eget. Phasellus ac lorem quis mi dapibus facilisis facilisis pellentesque sem. Vestibulum et lorem sed ipsum elementum luctus ut id lacus.',
          },
        },
      ],
    })

    dispatch('addSectionWithWidgets', {
      parentId: tabs.id,
      widgetList: [
        { type: 'Video' },
        {
          type: 'Text',
          options: {
            content:
              '<strong>Webinar 2</strong><br>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Etiam facilisis tempus erat, vitae lobortis orci elementum eget. Phasellus ac lorem quis mi dapibus facilisis facilisis pellentesque sem. Vestibulum et lorem sed ipsum elementum luctus ut id lacus.',
          },
        },
      ],
    })

    dispatch('addSectionWithWidgets', {
      parentId: tabs.id,
      widgetList: [
        { type: 'Video' },
        {
          type: 'Text',
          options: {
            content:
              '<strong>Webinar 3</strong><br>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Etiam facilisis tempus erat, vitae lobortis orci elementum eget. Phasellus ac lorem quis mi dapibus facilisis facilisis pellentesque sem. Vestibulum et lorem sed ipsum elementum luctus ut id lacus.',
          },
        },
      ],
    })

    Vue.nextTick(() => setTimeout(() => dispatch('ui/scrollIntoView', tabs.id, { root: true })))
    commit('reposition', reposition)

    dispatch(
      'history/pushToStack',
      {
        stack,
        action: 'landing/removeTabs',
        params: { id: tabs.id },
        stackToClear,
      },
      { root: true }
    )
  },

  addTabsItem({ commit, dispatch }, { id, index, stack = 'undo', stackToClear = 'redo' }) {
    commit('addTabsItem', { id, index })
    dispatch('addSection', { parentId: id })

    dispatch(
      'history/pushToStack',
      {
        stack,
        action: 'landing/removeTabsItem',
        params: { id, index },
        stackToClear,
      },
      { root: true }
    )
  },
}
